import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {DcPageService} from "../../../service/dc-page.service";
import {MenuItem, MenuItemCommandEvent, TreeNode} from "primeng/api";
import {DcPageDto, DcTreeNode} from "../../../model/dto/entity/dc-page.dto";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {TreeNodeContextMenuSelectEvent, TreeNodeExpandEvent} from "primeng/tree";
import {DcPageContentService} from "../../../service/dc-page-content.service";
import {DcPageContentDto} from "../../../model/dto/entity/dc-page-content.dto";
import {ContextMenu} from "primeng/contextmenu";
import {finalize, Subject, takeUntil} from "rxjs";
import {MvsUiObjectService} from "../../../../../core/shared/object/service/mvs-ui-object.service";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {DtoDetail} from "../../../../../core/shared/dto/dto.detail";
import {ObjectChangeInformation} from "../../../../../core/shared/logic/object-change-information";
import {MvsCrudModeEnum} from "../../../../../core/shared/service/crud/mvs-crud-mode.enum";
import {DtoImportObjectContext} from "../../../../../core/shared/dto/dto.import.object.context";
import {DtoImportObjectContextEntry} from "../../../../../core/shared/dto/dto.import.object.context.entry";
import {AutoCompleteCompleteEvent, AutoCompleteSelectEvent} from "primeng/autocomplete";
import {
    DcPageContentSearchResponse,
    DcPageSearchResult
} from "../../../model/dto/entity/dc-page-content-search-response.dto";
import {QlQueryDto} from "../../../../../core/shared/ql/data/dto/ql-query.dto";
import {QlWidgetFactory} from "../../../../../core/shared/widget/service/ql-widget.factory";
import {QlService} from "../../../../../core/shared/ql/service/ql.service";
import {ObjectIdentifier} from "../../../../../core/shared/basic/object-identifier";

type NodeKind = 'page' | 'newPage' | 'content' | 'newContent';

@Component({
    selector: 'dc-page-hierarchy-tree',
    templateUrl: './dc-page-hierarchy-tree.component.html',
    styleUrls: ['dc-page-hierarchy-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DcPageHierarchyTreeComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;  // indicator whether the component is busy
    initialized: boolean; // indicator whether the component was initialized
    routePageId: number;

    files: TreeNode[];
    items: MenuItem[];
    private destroy$ = new Subject<void>();
    showSidebar: boolean = true;
    suggestions: DcPageSearchResult[] = [];
    value: any;


    @Input() selectedNode: TreeNode;
    @Output() selectedNodeChange: EventEmitter<TreeNode> = new EventEmitter<TreeNode>();
    @Output() onNodeContentEntries = new EventEmitter<ObjectIdentifier[]>();
    @Output() emitHierarchyTree: EventEmitter<TreeNode[]> = new EventEmitter();
    @Output() sidebarToggle = new EventEmitter<boolean>();

    @ViewChild('cm') contextMenu: ContextMenu;

    constructor(
        protected dcPageService: DcPageService,
        protected route: ActivatedRoute,
        protected router: Router,
        protected dcPageContentService: DcPageContentService,
        protected qlService: QlService,
        protected objectService: MvsUiObjectService) {

    }

    ngOnInit(): void {
        this.initComponent();

        this.route.queryParams
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {

                this.mainPageProcessing(params);

                const page = params['page'];
                const update = params['update'];
                const contentID = params['content'];
                const showSidebar = params['sidebar'];

                if (showSidebar == "false") {
                    this.showSidebar = false;
                } else {
                    this.showSidebar = true;
                }
                this.sidebarToggle.emit(this.showSidebar);

                if (!page && !update && !contentID) {
                    return;
                }

                if (page && JSON.parse(update)) {
                    // this.retrievePageHierarchy(page, page, contentID);
                    this.retrievePageHierarchy(1, page, contentID, true, page);
                } else {
                    // this.retrievePageHierarchy(undefined, page, contentID, true, this.routePageId);
                    this.loadNavigationPath(page);
                }
            });

        this.objectService.objectChangeEvent.subscribe(params => {
           if (params.objectType == 'dc.DcPage') {
               //new dc page is created
               this.postPageCreated();
           }
        });
    }

    mainPageProcessing(params: Params) {
        const mainPageId = params['mainPageId'];

        if (!this.initialized) {
            // FIRST LOAD
            if (!mainPageId) {
                this.retrievePageHierarchy(1, null, null, false, null);
            } else {
                this.retrievePageHierarchy(1, null, null, true, +mainPageId);
            }
            return;
        }
    }

    /**
     * Initialize Component.
     */
    initComponent() {
        this.refreshComponent();
    }

    /**
     * Refresh Component.
     */
    refreshComponent() {
    }

    postPageCreated() {
        this.retrievePageHierarchy(1, null, null, true, +this.selectedNode.key);
    }

    retrievePageHierarchy(page: number = 1, expandPageId?: string, contentId?: string, forceRefresh: boolean = false, navigationId: number = null) {

        if (this.busy) {
            return;
        }
        this.busy = true;

        this.dcPageService.getPageHierarchy(page).subscribe((res: DcTreeNode<DcPageDto>) => {

            this.files = this.transformDataToTreeNodes([res], null);
            // this.getPageContentFromPage(this.files[0]);
            this.emitHierarchyTree.emit(this.files);

            if (expandPageId) {
                const found = this.expandTreeToFile(expandPageId, undefined, contentId);
                if (found) {
                    this.files = [...this.files];
                }
            }

            if (navigationId && forceRefresh) {
                this.loadNavigationPath(navigationId);
            } else {
                this.busy = false;
                this.initialized = true;
            }

        }, error => {
            this.busy = false;
        });
    }

    transformDataToTreeNodes(nodes: DcTreeNode<DcPageDto>[], parentNode: TreeNode | null): TreeNode[] {
        if (nodes.length > 0) {
            return nodes.map((node) => {
                const treeNode = this.createTreeNode(node.data, 'page', parentNode);
                return {...treeNode, children: this.transformDataToTreeNodes(node.children, treeNode)};
            });
        }
        return [];
    }

    handleNodeSelect() {
        if (this.selectedNode) {
            this.router.navigate(
                ['/dc/docs'],
                {
                    queryParams: { mainPageId: this.selectedNode.key },
                    queryParamsHandling: 'merge'
                }
            );
        }

        // if (this.selectedNode.checked) {
        //     this.emitContentEntries(this.selectedNode);
        // } else {
        this.getPageContentFromPage(this.selectedNode, null, true);
        // }

        this.selectedNodeChange.emit(this.selectedNode);
    }

    private extractPathIds(tree: any): string[] {
        const path: string[] = [];

        let node = tree;
        while (node) {
            path.push(node.data.id.toString());
            node = node.children?.find(c => c.children?.length > 0);
        }

        return path;
    }

    private expandUsingPath(pathIds: string[]) {
        let currentNodes = this.files;
        let lastFound: TreeNode | null = null;


        for (let id of pathIds) {
            const found = currentNodes?.find(n => n.key === id);

            if (found) {
                found.expanded = true;
                lastFound = found;
                currentNodes = found.children || [];
            }
        }


        if (lastFound) {
            this.selectedNode = lastFound;

            this.getPageContentFromPage(lastFound, null, true);

            this.selectedNodeChange.emit(lastFound);
        }

        // this.selectedNodeChange.emit(this.selectedNode);
        //
        // // Now load contents of the final page
        // this.onNodeContentEntries.emit(this.selectedNode?.data.contentNodes);
    }

    private loadNavigationPath(pageId: number) {

        this.dcPageService.getPageAncestors(pageId).subscribe(res => {
            this.busy = false;
            const navigationTree = res;

            // Convert to path array of page ID strings
            const pathIds = this.extractPathIds(navigationTree);

            // Expand and select nodes in the main big tree
            this.expandUsingPath(pathIds);

            // get content
            this.getPageContentFromPage(this.selectedNode, null, true);

            this.busy = false;
            this.initialized = true;
        }, error => {
            this.busy = false;
        });
    }

    // private storeContentEntries(entries: DcPageContentDisplayModeResponseDto[]) {
    //     if (!this.selectedNode) {
    //         return;
    //     }
    //
    //     // this.selectedNode.data.contentNodes = entries.map(e =>
    //     //     this.createTreeNode(e, 'content', this.selectedNode)
    //     // );
    //
    //     if (entries.length > 0) {
    //         this.onNodeContentEntries.emit(entries);
    //     }
    //     //
    //     // if (this.selectedNode.data.contentNodes && this.selectedNode.data.contentNodes.length > 0) {
    //     //     this.onNodeContentEntries.emit(this.selectedNode.data.contentNodes);
    //     // }
    //
    // }

    handleUnNodeSelect() {
        this.selectedNodeChange.emit(this.selectedNode);
    }

    handleNodeExpand(event: TreeNodeExpandEvent) {

        // if (event.node.checked) {
        //     return;
        // }

        this.getPageContentFromPage(event.node, null, false);
    }

    //     getPageContentFromPage(node: TreeNode, contentId?: string, emitEvent: boolean = false) {
    //     if (this.busy) {
    //         return;
    //     }
    //
    //     this.busy = true;
    //     node.checked = true;
    //
    //     const filterCriteria = FilterCriteria.create('page', FilterCriteria.cOperatorEqual, node.key);
    //     const objectRequest = new ObjectRequestList(false, [filterCriteria], [new Sorting('id', true)]);
    //
    //     this.dcPageContentService.list(objectRequest)
    //         .pipe(finalize(() => this.busy = false))
    //         .subscribe(res => {
    //             this.refreshNodeChildren(node, res.entries, contentId);
    //             this.busy = false;
    //             if (emitEvent) {
    //                 this.emitContentEntries(node);
    //             }
    //
    //             this.selectedNode = node;
    //             this.onNodeContentEntries.emit(this.selectedNode?.data.contentNodes);
    //
    //         }, error => {
    //             this.busy = false;
    //         });
    // }

    getPageContentFromPage(node: TreeNode, contentId?: string, emitEvent: boolean = false) {
        if (this.busy) {
            return;
        }

        this.busy = true;
        node.checked = true;

        const filterCriteria = FilterCriteria.create('e.pageId', FilterCriteria.cOperatorEqual, +node.key);

        const query: QlQueryDto = {
            name: "getPageContentFromPage",
            type: "view",
            start: {
                type: "entity",
                name: "dc.DcPageContent",
                as: "e",
                fields: [
                    {name: 'id'},
                    {name: 'orderPriority'},
                ]
            },
            filters: [filterCriteria],
        };

        const request = QlWidgetFactory.createObjectRequest(query);

        this.qlService.query(request)
            .pipe(finalize(() => this.busy = false))
            .subscribe(res => {
                const identifiers: ObjectIdentifier[] = [];
                this.busy = false;
                const entries = res.pipeData['getPageContentFromPage'];

                if (entries?.data?.length > 0) {
                    // return content identifiers
                    entries.data = entries.data.sort((a, b) => a.orderPriority - b.orderPriority);

                    for (const entry of entries.data) {
                        identifiers.push(new ObjectIdentifier('dc.DcPageContent', entry.id));
                    }

                }

                this.onNodeContentEntries.emit(identifiers)

            }, error => {
                this.busy = false;
            });
    }

    search(event: AutoCompleteCompleteEvent) {
            this.dcPageContentService.searchContent(event.query).subscribe((result: DcPageContentSearchResponse) => {
                if (result.results && result.results.length > 0) {
                    this.suggestions = [...result.results];
                } else {
                    this.suggestions = [];
                }
            });
    }

    selectContent(event: AutoCompleteSelectEvent) {
        if (event.value) {
            if (event.value.pageDtoId) {
                this.loadNavigationPath(event.value.pageDtoId);
                this.value = null;
                this.suggestions = [];
            }
        }
    }


    emitContentEntries(node: TreeNode) {
        const contentEntries = node.children?.filter(c => c.type === "content");

        if (contentEntries) {
            this.onNodeContentEntries.emit(node.data.contentNodes);
        }
    }

    private refreshNodeChildren(node: TreeNode, entries: DtoDetail[], contentId?: string) {
        // 1. Remove existing content children from the visible tree
        node.children = (node.children || []).filter(c => c.type !== 'content');

        // 2. Create TreeNode objects for all contents (but DO NOT attach to children[])
        node.data.contentNodes = entries.map(e =>
            this.createTreeNode(e, 'content', node)
        );

        // 3. Keep tree visually clean
        node.leaf = node.children.length === 0;

        if (contentId) {
            const found = node.data.contentNodes.find(c => c.key === contentId);
            if (found) {
                this.selectedNodeChange.emit(found);
            }
        }

        this.emitHierarchyTree.next(this.files);

    }

    handleGeneralPageCreate() {
        this.openCreateUpdateDialog(null, null);
    }

    openPageCreate(pageId: number = 0) {

        const importObjectContext = new DtoImportObjectContext([new DtoImportObjectContextEntry('dc.DcPage', Number(this.selectedNode.key))]);

        this.openCreateUpdateDialog(importObjectContext, pageId);

    }

    onPageEdit() {
        const pageId = +this.selectedNode.key;

        if (!pageId) {
            return;
        }

        this.openCreateUpdateDialog(null, pageId);
    }

    openCreateUpdateDialog(importObjectContext: DtoImportObjectContext, pageId: number = 0) {

        let mode: MvsCrudModeEnum = MvsCrudModeEnum.create;
        if (pageId) {
            mode = MvsCrudModeEnum.update;
        }

        this.objectService.openObjectViaDialog(
            importObjectContext,
            'dc.DcPage',
            pageId,
            null,
            false,
            false,
            (res: ObjectChangeInformation) => {

                this.postCreateUpdateDialog(res, mode);

            },
            mode,
            null,
            null,
            true
        );
    }

    postCreateUpdateDialog(res: ObjectChangeInformation, mode: MvsCrudModeEnum) {
        if (mode == MvsCrudModeEnum.create) {

            const childNode = this.createTreeNode(res.after, 'newPage', this.selectedNode);
            this.selectedNode.expanded = true;
            this.selectedNode.children.push(childNode);
            this.emitHierarchyTree.emit(this.files);


        } else if (mode == MvsCrudModeEnum.update) {
            // //find child node from parent
            // const childNode = this.selectedNode.parent?.children?.findIndex(item => item.key === this.selectedNode.key);
            //
            // //remove this selected node
            // if (childNode) {
            //     this.selectedNode.parent.children.splice(childNode, 1);
            // }
            // //rebuild again
            //
            // const newChildNode = this.createTreeNode(res.after, 'newPage', this.selectedNode.parent);
            // this.selectedNode = newChildNode;

            const parent = this.selectedNode.parent;

            if (parent?.children) {

                const index = parent.children.findIndex(c => c.key === this.selectedNode.key);

                if (index >= 0) {
                    // Remove old node
                    parent.children.splice(index, 1);

                    // Create new node
                    const newChildNode = this.createTreeNode(res.after, 'newPage', parent);

                    parent.children[index] = newChildNode;

                    // Update selectedNode reference
                    this.selectedNode = newChildNode;
                }
            }

        }


        this.files = [...this.files];

    }


    createPageContent() {
        if (this.busy) {
            return;
        }
        this.busy = true;

        const dto = new DcPageContentDto();

        dto.pageDtoId = Number(this.selectedNode.key);
        dto.content = "";
        dto.orderPriority = 1;

        this.dcPageContentService.create(dto).subscribe((res) => {
            this.busy = false;
            const contentNode = this.createTreeNode(res, 'newContent', this.selectedNode);
            this.selectedNodeChange.emit(contentNode);

            if (this.selectedNode) {
                this.getPageContentFromPage(this.selectedNode);
            } else if (this.selectedNode.children.length == 0) {
                this.selectedNode.leaf = false;
                this.selectedNode.expanded = true;
                this.getPageContentFromPage(this.selectedNode);
            }

        }, error => {
            this.busy = false;
        });
    }


    private createTreeNode<T>(data: T, kind: NodeKind, parent?: TreeNode): TreeNode {
        const isContent = kind === 'content' || kind === 'newContent';
        return {
            key: String((data as any).id),
            label: (data as any).title || String((data as any).id),
            icon: isContent ? 'pi pi-file' : 'pi pi-folder',
            parent,
            data,
            children: isContent ? null : [],
            leaf: isContent,
            type: kind,
            expanded: kind === 'page' && (data as any).id === 1
        };
    }


    handleContextMenu(event: TreeNodeContextMenuSelectEvent) {
        if (this.selectedNode && this.selectedNode.type !== 'content') {
            this.items = [
                {label: 'Add page', icon: 'pi pi-plus', command: () => this.openPageCreate()},
                {
                    label: 'Edit page',
                    icon: 'fa-regular fa-pen-to-square',
                    command: (event: MenuItemCommandEvent) => this.onPageEdit()
                },
                {
                    label: 'Create Content', icon: 'pi pi-file', command: () => {
                        this.createPageContent()
                    }
                }
            ];
        } else {
            this.contextMenu.hide();
            event.originalEvent.preventDefault();
            event.originalEvent.stopPropagation()
        }

    }

    expandTreeToFile(pageID: string, files: TreeNode[] = this.files, contentId?: string): TreeNode | null {
        for (const file of files) {
            file.children = file.children || [];
            if (file.key === pageID) {
                this.selectedNodeChange.emit(file);
                file.expanded = true;
                this.getPageContentFromPage(file, contentId);
                return file;
            }
            const node = this.expandTreeToFile(pageID, file.children || [], contentId);
            if (node) {
                file.expanded = true;
                this.getPageContentFromPage(file);
                return node;
            }
        }

        return null;
    }

    trackByKey(_: number, node: TreeNode) {
        return node.key;
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
    }

    toggleSidebar(): void {
        this.showSidebar = !this.showSidebar;

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                sidebar: this.showSidebar,
            },
            queryParamsHandling: 'merge' // if you want to keep existing query params
        });

        // // AFTER toggling, emit the new state
        // this.sidebarToggle.emit(this.showSidebar);
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }


}
